import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 7];
const DAY_KEYS = {
  1: "mondayShort",
  2: "tuesdayShort",
  3: "wednesdayShort",
  4: "thursdayShort",
  5: "fridayShort",
  6: "saturdayShort",
  7: "sundayShort",
};

const pad = (n) => String(n).padStart(2, "0");

/**
 * Controlled editor for a `opening_hours` array of 7 day objects.
 * Value shape: [{ day_of_week, closed, intervals: [{ from_hour, from_minute, to_hour, to_minute }] }]
 */
export function OpeningHoursEditor({ value, onChange, disabled = false, testIdPrefix = "oh" }) {
  const { t } = useI18n();

  // Normalize to a full 7-day map
  const byDay = useMemo(() => {
    const map = new Map();
    for (const d of value || []) map.set(d.day_of_week, d);
    return DAY_ORDER.map(
      (dow) =>
        map.get(dow) || {
          day_of_week: dow,
          closed: dow === 7,
          intervals: [],
        },
    );
  }, [value]);

  const update = (dow, patch) => {
    const next = byDay.map((d) => (d.day_of_week === dow ? { ...d, ...patch } : d));
    onChange(next);
  };

  const updateInterval = (dow, idx, patch) => {
    const day = byDay.find((d) => d.day_of_week === dow);
    const nextIv = day.intervals.map((iv, i) => (i === idx ? { ...iv, ...patch } : iv));
    update(dow, { intervals: nextIv });
  };

  const addInterval = (dow) => {
    const day = byDay.find((d) => d.day_of_week === dow);
    update(dow, {
      intervals: [
        ...day.intervals,
        { from_hour: 9, from_minute: 0, to_hour: 18, to_minute: 0 },
      ],
    });
  };

  const removeInterval = (dow, idx) => {
    const day = byDay.find((d) => d.day_of_week === dow);
    update(dow, { intervals: day.intervals.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-2" data-testid={testIdPrefix + "-editor"}>
      {byDay.map((day) => (
        <div
          key={day.day_of_week}
          className="grid grid-cols-[70px_100px_1fr] items-start gap-3 border-b border-border py-2 last:border-b-0"
          data-testid={`${testIdPrefix}-day-${day.day_of_week}`}
        >
          <div className="pt-2 text-sm font-mono uppercase tracking-widest text-muted-foreground">
            {t(`common.${DAY_KEYS[day.day_of_week]}`)}
          </div>
          <div className="pt-1.5">
            <label className="flex items-center gap-2 text-xs">
              <Switch
                checked={!day.closed}
                disabled={disabled}
                onCheckedChange={(v) => update(day.day_of_week, { closed: !v, intervals: v ? day.intervals.length ? day.intervals : [{ from_hour: 9, from_minute: 0, to_hour: 18, to_minute: 0 }] : [] })}
                data-testid={`${testIdPrefix}-day-${day.day_of_week}-open`}
              />
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {day.closed ? t("common.closed") : t("common.open")}
              </span>
            </label>
          </div>
          <div className="space-y-2">
            {day.closed && (
              <span className="text-xs text-muted-foreground">—</span>
            )}
            {!day.closed &&
              day.intervals.map((iv, idx) => (
                <div key={`${iv.from_hour}:${iv.from_minute}-${iv.to_hour}:${iv.to_minute}-${idx}`} className="flex items-center gap-2" data-testid={`${testIdPrefix}-day-${day.day_of_week}-iv-${idx}`}>
                  <Input
                    type="time"
                    disabled={disabled}
                    value={`${pad(iv.from_hour)}:${pad(iv.from_minute)}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(":").map((s) => parseInt(s, 10) || 0);
                      updateInterval(day.day_of_week, idx, { from_hour: h, from_minute: m });
                    }}
                    className="h-8 w-28 font-mono text-xs"
                  />
                  <span className="text-muted-foreground">→</span>
                  <Input
                    type="time"
                    disabled={disabled}
                    value={`${pad(iv.to_hour)}:${pad(iv.to_minute)}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(":").map((s) => parseInt(s, 10) || 0);
                      updateInterval(day.day_of_week, idx, { to_hour: h, to_minute: m });
                    }}
                    className="h-8 w-28 font-mono text-xs"
                  />
                  {!disabled && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => removeInterval(day.day_of_week, idx)}
                      data-testid={`${testIdPrefix}-day-${day.day_of_week}-remove-${idx}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            {!day.closed && !disabled && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => addInterval(day.day_of_week)}
                className="h-7 gap-1 px-2 text-xs"
                data-testid={`${testIdPrefix}-day-${day.day_of_week}-add`}
              >
                <Plus className="h-3 w-3" />
                {t("dealership.addInterval")}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
